<script>
	import { onMount } from 'svelte';
	import { writable } from 'svelte/store';
	import { pageOffset, firstBottomBoxVisible } from '../js/model/stores';
	import { animateElement, freezeElement } from '../js/utils/animate';

	export let text;
	export let linkUrl;
	export let linkText;
	export let insta;
	export let facebook;
	export let twitter;
	export let image;
	export let size;
	export let timeOffset = 0;
	export let video = "false";

	let _box;
	let _contentScene;
	let _backgroundImage;

	let isVisible = writable(false);

	let animation = (value) => {

		let animationFunction = $isVisible ? freezeElement : animateElement;
		let targetVisibility = $isVisible ? 0 : 0.2;

		animationFunction(value, _box, (currentPageOffset) => {

			if(!$firstBottomBoxVisible) {
				firstBottomBoxVisible.set(true);
			}

			isVisible.set(!$isVisible);

			if($isVisible && video !== "false" && _box.querySelector('video')) {

				// TODO: check autoplay conditions. We may use a scroll event to trigger the playback
				_box.querySelector('video').play();
			}
		}, targetVisibility);
	};

	let prepareSceneContent = () => {
		if (!_contentScene) {
			requestAnimationFrame(prepareSceneContent);
			return;
		}

		pageOffset.subscribe(animation);

		// Store variables trigger on changes only, regardless of how often the setter has been called with the same value.
		isVisible.subscribe((state) => {

			if (state) {
				_box.classList.add('box--in-view');
			}
		});

		setTimeout(() => {
			animation($pageOffset);
		}, 100);
	};

	onMount(() => {
		if (_backgroundImage) {
			_backgroundImage.style.backgroundImage = `url(${image}`;
		}

		prepareSceneContent();
	});
</script>

<style>
	:global(.box) {
		width: 100%;
		padding-top: 100%;
		position: relative;
		flex: 1 1 450px;

		opacity: 0;

		transition: opacity .7s 0.1s;
	}

	:global(.box:nth-child(odd)) {
		transition: opacity .7s 0.3s;
	}

	:global(.box--big) {
		flex: 1 1 100%;
	}

	:global(.box--in-view) {
		opacity: 1;
	}

	:global(.box__background_image) {
		position: absolute;
		left: -5% !important;
		top: -5% !important;
		width: 110%;
		height: 110%;
		z-index: 0;
		background-size: cover;
		transition: transform .2s;
	}

	:global(.box:hover .box__background_image) {
		transform: scale(0.95);
	}

	:global(.box__background_filter) {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(14, 28, 79, .5);
		z-index: 0;
	}

	:global(.box__background_video) {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(14, 28, 79, .5);
		z-index: 0;
		overflow: hidden;
	}

	:global(.box__background_video video) {
		min-width: 100%;
		min-height: 100%;
	}

	:global(.box:hover .box__background_filter) {
		background-color: rgba(14, 28, 79, .2);
		transition: background-color .5s;
	}

	.box__content {
		position: absolute;
		left: 0 !important;
		top: 0 !important;
		width: 100%;
		height: 100%;
		z-index: 1;
		pointer-events: all !important;
		text-shadow: #010831 0 0 40px;
	}

	.box__content__parallax {
		position: absolute !important;
		width: 80%;
		left: 10% !important;
		top: 25% !important;
		text-align: center;
		color: #FFF;
		text-shadow: var(--color-black) 0 0 0.2em;
		z-index: 1;
		transition: transform .2s;
	}

	:global(.box:hover .box__content__parallax) {
		transform: scale(1.05);
	}

	.box__background_image__container {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(14, 28, 79, .5);
		z-index: 0;
		overflow: hidden;
	}

	:global(.box__content__header) {
		color: var(--color-white);
		hyphens: manual;
		margin: 0;
	}

	:global(.box__content__link) {
		text-decoration: underline;
		margin: 0;
	}

	:global(.box__content__social) {
		width: 65%;
		margin-top: 2.5em;
		height: 18em;
		max-width: 190px;
		max-height: 75px;
		margin-left: 50%;
		transform: translateX(-50%);
		display: flex;
		flex-wrap: wrap;
	}

	:global(.box--big .box__content__social) {
		width: 95%;
		max-width: 300px;
	}

	:global(.box__content__social__link) {
		position: relative;
		height: 100%;
		width: 50%;
		background-size: contain;
		background-position: center;
		background-repeat: no-repeat;
		float: left;
		flex: 1;
	}
	:global(.box__content__social__link:after) {
		content: '';
		position: absolute;
		background: inherit;
		left: 50%;
		width: 100%;
		height: 100%;
		z-index: -1;
		transform: translateX(-50%);
		filter: blur(40px) brightness(0);
		border-radius: 10px;
	}

	:global(.box__content__social__link__instagram) {
		background-image: url('../icons/social_instagram.svg');
	}

	:global(.box__content__social__link__facebook) {
		background-image: url('../icons/social_facebook.svg');
	}

	:global(.box__content__social__link__twitter) {
		background-image: url('../icons/social_twitter.svg');
	}

	@media (min-width: 900px) {
		:global(.box) {
			padding-top: calc(100% / 2);
		}

		:global(.box__content) {
			top: 20% !important;
		}

		:global(.box__content__social) {
			height: 12em;
		}
	}

	@media (min-width: 1350px) {
		:global(.box) {
			padding-top: calc(100% / 3);
			flex: 1 1 calc(100% / 3);
		}

		:global(.box--big) {
			flex: 1 1 100%;
		}

		:global(.box__content) {
			top: 25% !important;
		}

		:global(.box__content__social) {
			height: 6em;
		}
	}
</style>

<div class="box box--{size}" bind:this={_box} target="_blank">
	<a class="box__content" bind:this={_contentScene} id="bottom-box-content-{Math.random()}" href="{linkUrl}">
		<div class="box__content__parallax">
			<h2 class="box__content__header">{@html text}</h2>
			<p class="box__content__link">{linkText}</p>
			<div class="box__content__social">
				{#if insta}
					<a class="box__content__social__link box__content__social__link__instagram" href="{insta}" target="_blank"></a>
					{/if}
				{#if facebook}
					<a class=" box__content__social__link  box__content__social__link__facebook" href="{facebook}" target="_blank"></a>
				{/if}
				{#if twitter}
					<a class=" box__content__social__link  box__content__social__link__twitter" href="{twitter}" target="_blank"></a>
				{/if}
			</div>
		</div>
	</a>
	{#if video == "true"}
		<div class="box__background_video">
			<video controls="">
				<!-- MP4 must be first for iPad! -->
				<source src="https://video-ber1-1.xx.fbcdn.net/v/t39.24130-2/117752486_942220689629894_5288738988820459491_n.mp4?_nc_cat=106&_nc_sid=985c63&efg=eyJ2ZW5jb2RlX3RhZyI6Im9lcF9oZCJ9&_nc_ohc=xg_mkZV81CwAX_EIwiI&_nc_ht=video-ber1-1.xx&oh=c111b89acf08510b5febaf9fcdf6049e&oe=5F6F8CEA" type="video/mp4"><!-- Safari / iOS, IE9 -->
<!--				<source src="http://clips.vorwaerts-gmbh.de/VfE.webm" type="video/webm">&lt;!&ndash; Chrome10+, Ffx4+, Opera10.6+ &ndash;&gt;-->
<!--				<source src="http://clips.vorwaerts-gmbh.de/VfE.ogv" type="video/ogg">&lt;!&ndash; Firefox3.6+ / Opera 10.5+ &ndash;&gt;-->
<!--				&lt;!&ndash; fallback to Flash: &ndash;&gt;-->
<!--				<object type="application/x-shockwave-flash" data="player.swf">-->
<!--&lt;!&ndash;					&lt;!&ndash; Firefox uses the `data` attribute above, IE/Safari uses the param below &ndash;&gt;&ndash;&gt;-->
<!--&lt;!&ndash;					<param name="movie" value="player.swf">&ndash;&gt;-->
<!--&lt;!&ndash;					<param name="flashvars" value="autostart=true&amp;controlbar=over&amp;image=poster.jpg&amp;file=http://clips.vorwaerts-gmbh.de/VfE_flash.mp4">&ndash;&gt;-->
<!--					&lt;!&ndash; fallback image &ndash;&gt;-->
<!--					<img src="{image}" alt="Big Buck Bunny" title="No video playback capabilities, please download the video below" width="640" height="360">-->
<!--				</object>-->
			</video>
		</div>
		<div class="box__background_filter"></div>
	{:else}
		<div class="box__background_image__container">
			<div class="box__background_image" bind:this={_backgroundImage}></div>
		</div>
		<div class="box__background_filter"></div>
	{/if}
</div>
